import update from 'immutability-helper';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Icon, { CloseOutlined } from '@ant-design/icons';
import Table from '@components/common/DimensionTable';
import {
  EDimensionListFilter,
  TTableFilter,
} from '@components/common/DimensionTable/components/TableSubHeader/typed';
import { ETitleColor } from '@components/common/DimensionTable/components/Title';
import {
  ETableSize,
  IDimensionInfo,
  eventDimensionNames,
  tagDimensionNames,
} from '@components/dimensions/typed';
import { partitionOptions } from '@components/dimensions/utils/partition';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useGetAllDataTransform,
  useGetBlockEventsName,
  useGetMappedEvents,
  useGetMappedStatusEvent,
  usePostDimensionData,
} from '@src/api/emp/queryHooks';
import {
  EDimensionType,
  ERenamedDimension,
  ResetDimensionForMappedEvent,
  mappedEventDimensionType,
} from '@src/common/constant/enum';
import { useCustomDimensionList } from '@src/emp-3.0/api/queryHooks';
import DragHandlerIcon from '@src/icons/emp/drag_handler.svg';
import METRIC from '@src/utils/metricKeys';
import { applyFilter } from '@store/common/actions';
import commonSelector from '@store/common/selectors';
import { getFilterFromRules, getRulesFromFilter, track } from '@utils';
import { checkIfRuleAlreadyExist, createFilterRule } from '@utils/filter';
import { APPLY_DRILL_DOWN } from '@utils/track';

import styles from './index.less';
import { useIsNewWorkflowPage } from '@src/hooks/useNewWorkflow';

interface IDimensionTableContainer {
  data: IDimensionInfo;
  size: ETableSize;
  dimensionList: any[];
  onTitleChange: (title: string) => void;
  onClose: () => void;
  lockedDimensionNames: string[];
  eventAttributeDimensionNames: string[];
  transformPanelOpen: boolean;
  column?: 3 | 4;
  enableAction?: boolean;
}

export const DimensionTableContainer: React.FC<IDimensionTableContainer> = (
  props: IDimensionTableContainer,
) => {
  const {
    data,
    size,
    dimensionList = [],
    onTitleChange,
    onClose,
    lockedDimensionNames,
    eventAttributeDimensionNames,
    transformPanelOpen,
    column = 4,
    enableAction = true,
  } = props;
  const isFlowPage = useIsNewWorkflowPage();
  const { dimensionName, dimensionType } = data;

  const dispatch = useDispatch();

  const [dimensionData, setDimensionData] = useState<any[]>([]);
  const [activeTitleFilter, setTitleActiveFilter] = useState<TTableFilter>(
    EDimensionListFilter.ALL,
  );

  const { data: allMappedEvents, isFetching: isFetchingAllMappedEventsData } = useGetMappedEvents({
    need_all_storage: 1,
  });
  const { data: mappedEventNameData } = useGetMappedStatusEvent('event_name');
  const { data: mappedTagKeyData } = useGetMappedStatusEvent('tag_key');
  const { data: mappedTagValueData } = useGetMappedStatusEvent('tag_value');
  const { data: blockedEventsName } = useGetBlockEventsName();
  const { data: customDimensionList } = useCustomDimensionList();
  const { data: pageTransformedData, isFetching: isFetchingPageData } = useGetAllDataTransform(
    ERenamedDimension.edited_page_title,
  );
  const { data: screenTransformedData, isFetching: isFetchingScreenData } = useGetAllDataTransform(
    ERenamedDimension.edited_screen_title,
  );
  const { mutate: getDimensionData, isLoading: isGettingDimensionData } = usePostDimensionData();
  const common = useSelector(commonSelector);
  const isFirstFetch = useRef<boolean>(true);

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: dimensionName,
  });

  const dndStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isEventDimension = eventDimensionNames.includes(dimensionName);

  const isFetching = isFetchingPageData || isFetchingScreenData || isFetchingAllMappedEventsData;

  const mappedStatusList = useMemo(() => {
    switch (dimensionName) {
      case mappedEventDimensionType.merged_event_tag_key:
        return mappedTagKeyData?.data;
      case mappedEventDimensionType.merged_event_tag_value:
        return mappedTagValueData?.data;
      case mappedEventDimensionType.all_event_name:
      case mappedEventDimensionType.conviva_event_name:
      case mappedEventDimensionType.custom_event_name:
        return mappedEventNameData?.data;
      default:
        return [];
    }
  }, [dimensionName, mappedEventNameData, mappedTagKeyData, mappedTagValueData]);

  const deployedStatusList = useMemo(() => {
    switch (dimensionName) {
      case ERenamedDimension.edited_page_title:
        return (pageTransformedData as any)?.data ?? [];
      case ERenamedDimension.edited_screen_title:
        return (screenTransformedData as any)?.data ?? [];
      default:
        return [];
    }
  }, [dimensionName, pageTransformedData, screenTransformedData]);

  useEffect(() => {
    if (!dimensionName) return;
    setDimensionData([]);
    getDimensionData(
      { fields: [dimensionName] },
      {
        onSuccess: (data) => {
          setDimensionData(data?.data?.[0] ?? []);
        },
        onError: () => {
          setDimensionData([]);
        },
      },
    );
  }, [dimensionName, common.interval, common.appliedFilters]);

  // refetch data when post or put or delete mapped events
  // allMappedEvents will be refetch when mapped events change
  // we can refetch data according to the change of allMappedEvents
  useEffect(() => {
    const refetch =
      ([...eventDimensionNames, ...tagDimensionNames].includes(dimensionName) && allMappedEvents) ||
      // after renaming page/screen attribute, we should refetch related dimensions
      dimensionType === EDimensionType.CUSTOM_DIMENSION;
    // when we get allMappedEvents first time, refetch is not necessary
    if (refetch && isFirstFetch.current && !isFetching) {
      isFirstFetch.current = false;
      return;
    }
    if (refetch && !isFirstFetch.current && !isFetching) {
      getDimensionData(
        { fields: [dimensionName], refetch },
        {
          onSuccess: (data) => {
            setDimensionData(data?.data?.[0] ?? []);
          },
          onError: () => {
            setDimensionData([]);
          },
        },
      );
    }
  }, [isFetching, allMappedEvents, pageTransformedData, screenTransformedData]);

  const genDimensionData = (extraData: any[], key: string) => {
    let res = extraData.reduce((res, v) => {
      if (!res?.find((val) => val[dimensionName] === v[key])) {
        return [
          ...res,
          {
            [dimensionName]: v[key],
            [METRIC.AppTotalUserEvents]: 0,
          },
        ];
      }
      return res;
    }, []);
    res = res?.filter((event) => {
      return !dimensionData?.find((v) => v[dimensionName] === event[dimensionName]);
    });
    return [...(dimensionData ?? []), ...res];
  };

  const resDimensionData = useMemo(() => {
    let mappedEventsData = allMappedEvents?.data ?? [];
    let editedPageTitleData = (pageTransformedData as any)?.data ?? [];
    let editedScreenTitleData = (screenTransformedData as any)?.data ?? [];

    const rules = common?.appliedFilters?.[0]?.rule ?? [];
    rules?.forEach((val) => {
      mappedEventsData = mappedEventsData.filter((event) => {
        return val.some((v) => {
          return (
            (v.field === 'mapped_event' && v.op === 'eq' && v.value === event.event_name) ||
            v.field !== 'mapped_event'
          );
        });
      });
      editedPageTitleData = editedPageTitleData.filter((event) => {
        return val.some((v) => {
          return (
            (v.field === ERenamedDimension.edited_page_title &&
              v.op === 'eq' &&
              v.value === event.name) ||
            v.field !== ERenamedDimension.edited_page_title
          );
        });
      });
      editedScreenTitleData = editedScreenTitleData.filter((event) => {
        return val.some((v) => {
          return (
            (v.field === ERenamedDimension.edited_screen_title &&
              v.op === 'eq' &&
              v.value === event.name) ||
            v.field !== ERenamedDimension.edited_screen_title
          );
        });
      });
    });

    if (
      mappedEventsData.length > 0 &&
      dimensionName === ResetDimensionForMappedEvent.mapped_event
    ) {
      return genDimensionData(mappedEventsData, 'event_name');
    } else if (
      mappedEventsData.length > 0 &&
      dimensionName === ResetDimensionForMappedEvent.event_category
    ) {
      return genDimensionData(mappedEventsData, 'event_category');
    } else if (
      editedPageTitleData.length > 0 &&
      dimensionName === ERenamedDimension.edited_page_title
    ) {
      return genDimensionData(editedPageTitleData, 'name');
    } else if (
      editedScreenTitleData.length > 0 &&
      dimensionName === ERenamedDimension.edited_screen_title
    ) {
      return genDimensionData(editedScreenTitleData, 'name');
    } else {
      return [...(dimensionData ?? [])];
    }
  }, [
    dimensionName,
    dimensionData,
    common.appliedFilters,
    pageTransformedData,
    screenTransformedData,
  ]);

  const heightStyle = useMemo(() => {
    if (isFlowPage) {
      return 'calc(100vh - 250px)';
    }
    let height = '450px';
    switch (size) {
      case ETableSize.LARGE:
        height = '750px';
        break;
      case ETableSize.MEDIUM:
        height = '600px';
        break;
      case ETableSize.AUTO:
      default:
        break;
    }
    return height;
  }, [size]);

  const { appliedFilters } = common;
  const appliedFiltersRef = useRef(appliedFilters);
  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);
  const handleRowClick = useCallback(
    ({ dimensionName, name }) => {
      const oFilter = appliedFiltersRef.current?.[0].rule;
      const currentFilter = createFilterRule(dimensionList, dimensionName, dimensionName, name);
      const doesRuleExist = checkIfRuleAlreadyExist(oFilter, currentFilter);
      let filter;
      if (!doesRuleExist) {
        filter = update(oFilter, {
          $push: [[currentFilter]],
        });
      } else {
        filter = oFilter
          .map((andFilter) => {
            return andFilter.filter((orFilter) => {
              return (
                orFilter.field !== dimensionName ||
                (orFilter.field === dimensionName && orFilter.value !== name)
              );
            });
          })
          .filter((subArray) => subArray.length > 0);
      }
      const allRules = getRulesFromFilter(filter, true);
      const filterToApply = getFilterFromRules(allRules, true);
      dispatch(applyFilter(filterToApply));
      track({ event: APPLY_DRILL_DOWN });
    },
    [dimensionList, appliedFilters, dispatch],
  );

  const titleOptions = useMemo(() => {
    const { defaultOptions, options, systemOptions, userDefinedOptions } = partitionOptions(
      dimensionList,
      (dimension) => !isEventDimension && lockedDimensionNames?.includes(dimension.dimension_name),
    );
    if (eventDimensionNames.includes(dimensionName)) {
      return defaultOptions;
    }
    if (activeTitleFilter === EDimensionListFilter.System) {
      return systemOptions;
    } else if (activeTitleFilter === EDimensionListFilter.Custom) {
      return userDefinedOptions;
    }
    return options;
  }, [dimensionList, dimensionName, activeTitleFilter, isEventDimension, lockedDimensionNames]);

  const lock = lockedDimensionNames?.includes(dimensionName);
  let titleColor = ETitleColor.DEFAULT;
  if (
    dimensionType === EDimensionType.CUSTOM_DIMENSION &&
    lockedDimensionNames?.includes(dimensionName) &&
    transformPanelOpen
  ) {
    titleColor = ETitleColor.GREEN;
  }
  if (
    dimensionType === EDimensionType.EVENT_ATTRIBUTE &&
    eventAttributeDimensionNames?.includes(dimensionName) &&
    transformPanelOpen
  ) {
    titleColor = ETitleColor.BLUE;
  }

  const draggableProps = { ...attributes, ...listeners };

  const selectedList = useMemo(() => {
    const filtersUnderDimensionName: string[] = [];
    common.appliedFilters[0].rule.forEach((andFilters) => {
      andFilters.forEach((orFilter) => {
        if (orFilter?.field === dimensionName) {
          filtersUnderDimensionName.push(orFilter?.value);
        }
      });
    });
    return filtersUnderDimensionName;
  }, [common.appliedFilters, dimensionName]);

  const transformedData = {
    [ERenamedDimension.edited_page_title]: (pageTransformedData as any)?.data,
    [ERenamedDimension.edited_screen_title]: (screenTransformedData as any)?.data,
  };

  const handleTitleFilterClick = (value: TTableFilter) => {
    setTitleActiveFilter(value);
  };

  return (
    <div
      className={styles.rootContainer}
      key={dimensionName + dimensionType}
      id={dimensionName}
      ref={setNodeRef}
      style={{ ...dndStyle, height: heightStyle, width: `${100 / column}%` }}
    >
      <div className={styles.whiteBackground}>
        <div className={styles.topBar}>
          {lock ? null : (
            <Icon className={styles.dragIcon} component={DragHandlerIcon} {...draggableProps} />
          )}
        </div>
        <div style={{ height: '100%' }}>
          <Table
            enableAction={enableAction}
            dimension={data}
            dimensionList={dimensionList}
            lock={lock}
            loading={isGettingDimensionData}
            titleDefaultValue={dimensionName}
            onTitleChange={onTitleChange}
            titleOptions={titleOptions}
            titleColor={titleColor as any}
            data={resDimensionData}
            customDimensionList={customDimensionList}
            mappedStatusList={mappedStatusList}
            deployedStatusList={deployedStatusList}
            blockedEventsData={blockedEventsName}
            mappedEventDimensionData={allMappedEvents?.data}
            transformedData={transformedData}
            selectedList={selectedList}
            onRowClick={handleRowClick}
            onTitleFilterClick={handleTitleFilterClick}
          />
        </div>
        {lock ? null : <CloseOutlined className={styles.closeBtn} onClick={onClose} />}
      </div>
    </div>
  );
};
